import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, CheckCircle, XCircle, ClockCounterClockwise, ArrowLeft, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestRequestsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full detail
  const [savingReview, setSavingReview] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/test-requests`);
      setItems(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load test requests');
    } finally { setLoading(false); }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/test-requests/${id}`);
      setSelected(res.data);
      setReviewNote(res.data.review_note || '');
    } catch { toast.error('Failed to load'); }
  };

  const saveReview = async (status) => {
    if (!selected) return;
    setSavingReview(true);
    try {
      await axios.put(`${API}/admin/test-requests/${selected.id}/review`, { review_status: status, review_note: reviewNote });
      toast.success(`Marked as ${status}`);
      setSelected(s => ({ ...s, review_status: status, review_note: reviewNote }));
      fetchList();
    } catch { toast.error('Failed to save review'); }
    finally { setSavingReview(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this test request permanently?')) return;
    try {
      await axios.delete(`${API}/admin/test-requests/${id}`);
      toast.success('Deleted');
      setSelected(null);
      fetchList();
    } catch { toast.error('Failed'); }
  };

  useEffect(() => { fetchList(); }, []);

  const filtered = useMemo(() =>
    statusFilter === 'all' ? items : items.filter(i => i.review_status === statusFilter),
    [items, statusFilter]
  );

  const publicUrl = `${window.location.origin}/english-assessment`;

  if (selected) return (
    <DetailView item={selected} onBack={() => setSelected(null)} onSaveReview={saveReview} onDelete={() => remove(selected.id)}
                reviewNote={reviewNote} setReviewNote={setReviewNote} saving={savingReview} />
  );

  return (
    <div className="space-y-6" data-testid="test-requests-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Test Requests</h1>
          <p className="text-sm text-gray-500 mt-1">Public English Assessment submissions awaiting review.</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2 max-w-lg">
          <span className="text-xs text-gray-500 shrink-0">Public link:</span>
          <code className="text-xs text-blue-700 truncate flex-1">{publicUrl}</code>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Copied'); }} data-testid="copy-assessment-link">Copy</Button>
        </div>
      </div>

      <div className="flex gap-2 text-xs">
        {['all', 'pending', 'pass', 'fail'].map(f => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={`px-3 py-1 rounded-full border ${statusFilter === f ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400"><CircleNotch size={22} className="animate-spin mx-auto mb-2" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-300 rounded-lg text-gray-400">
          <p className="text-sm">No test requests yet.</p>
          <p className="text-xs mt-1">Share the public link above with a student to receive their first submission.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Student</th>
                <th className="px-4 py-3 text-left">Submitted</th>
                <th className="px-4 py-3 text-left">Reading</th>
                <th className="px-4 py-3 text-left">Writing</th>
                <th className="px-4 py-3 text-left">Review</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(i => {
                const r = i.section_scores?.reading; const w = i.section_scores?.writing;
                return (
                  <tr key={i.id} className="hover:bg-blue-50/40 cursor-pointer" onClick={() => openDetail(i.id)} data-testid={`test-row-${i.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{i.first_name} {i.last_name}</p>
                      <p className="text-[11px] text-gray-500">{i.email} · {i.phone}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{i.submitted_at ? new Date(i.submitted_at).toLocaleString() : <span className="text-gray-400 italic">in progress</span>}</td>
                    <td className="px-4 py-3 text-gray-800">{r ? `${r.score}/${r.total}` : '—'}</td>
                    <td className="px-4 py-3 text-gray-800">{w ? `${w.score}/${w.total}` : '—'}</td>
                    <td className="px-4 py-3">
                      <StatusPill status={i.review_status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    pending: { icon: ClockCounterClockwise, cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Pending' },
    pass:    { icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Pass' },
    fail:    { icon: XCircle, cls: 'bg-red-100 text-red-700 border-red-200', label: 'Fail' },
  }[status || 'pending'];
  const Icon = cfg.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.cls}`}><Icon size={11} weight="fill" /> {cfg.label}</span>;
}

function DetailView({ item, onBack, onSaveReview, onDelete, reviewNote, setReviewNote, saving }) {
  const bank = useMemo(() => Object.fromEntries((item.question_bank || []).map(q => [q.id, q])), [item.question_bank]);
  const r = item.section_scores?.reading; const w = item.section_scores?.writing;
  return (
    <div className="space-y-5" data-testid="test-request-detail">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft size={14} className="mr-1" /> Back</Button>
        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={onDelete}><Trash size={14} className="mr-1" /> Delete</Button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-xl font-bold text-gray-900">{item.first_name} {item.last_name}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-3 text-sm">
          <Field label="Email" value={item.email} />
          <Field label="Phone" value={item.phone} />
          <Field label="Date of Birth" value={item.dob} />
          <Field label="Submitted" value={item.submitted_at ? new Date(item.submitted_at).toLocaleString() : '—'} />
          <Field label="Reading" value={r ? `${r.score} / ${r.total}` : '—'} />
          <Field label="Writing" value={w ? `${w.score} / ${w.total}` : '—'} />
          <Field label="Review Status" value={<StatusPill status={item.review_status} />} />
          <Field label="Reviewed By" value={item.reviewed_by || '—'} />
        </div>
      </div>

      {/* Review controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <p className="text-sm font-semibold text-gray-900">Review</p>
        <textarea
          className="w-full min-h-[80px] p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Notes on the essay, overall performance, etc."
          value={reviewNote}
          onChange={e => setReviewNote(e.target.value)}
          data-testid="review-note-input"
        />
        <div className="flex gap-2">
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => onSaveReview('pass')} disabled={saving} data-testid="mark-pass"><CheckCircle size={14} className="mr-1" /> Mark Pass</Button>
          <Button variant="destructive" onClick={() => onSaveReview('fail')} disabled={saving} data-testid="mark-fail"><XCircle size={14} className="mr-1" /> Mark Fail</Button>
          <Button variant="outline" onClick={() => onSaveReview('pending')} disabled={saving} data-testid="mark-pending"><ClockCounterClockwise size={14} className="mr-1" /> Reset to Pending</Button>
        </div>
      </div>

      {/* Essay */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-sm font-semibold text-gray-900 mb-2">WritePlacer Essay <span className="text-[11px] text-gray-400 font-normal">· {(item.essay || '').trim().split(/\s+/).filter(Boolean).length} words</span></p>
        <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded p-3">{item.essay || <span className="italic text-gray-400">No essay submitted.</span>}</p>
      </div>

      {/* Per-question breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-sm font-semibold text-gray-900 mb-3">Answers</p>
        <div className="space-y-3">
          {(item.answers || []).map((a, i) => {
            const q = bank[a.question_id];
            if (!q) return null;
            return (
              <div key={a.question_id} className={`border rounded-md p-3 text-sm ${a.is_correct ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/40'}`}>
                <p className="font-semibold text-gray-900 mb-2">Q{i + 1} ({q.section}) — {q.question}</p>
                {q.prompt && <p className="text-[12px] italic text-gray-600 mb-2 whitespace-pre-line">{q.prompt}</p>}
                <div className="grid grid-cols-1 gap-1">
                  {(q.options || []).map(opt => {
                    const isSelected = a.selected_option_id === opt.id;
                    const isCorrect  = a.correct_option_id === opt.id;
                    const cls = isCorrect ? 'text-emerald-800 font-semibold'
                              : isSelected ? 'text-red-700 line-through'
                              : 'text-gray-600';
                    return <p key={opt.id} className={`text-[12px] ${cls}`}>{isCorrect ? '✓ ' : isSelected ? '✗ ' : '  '}{opt.text}</p>;
                  })}
                </div>
              </div>
            );
          })}
          {(!item.answers || item.answers.length === 0) && <p className="text-xs text-gray-400 italic">No MCQ answers recorded.</p>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
      <div className="text-sm text-gray-900">{value}</div>
    </div>
  );
}
